import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { ModalDisplayedTracker } from '@zenbusiness/application-commons-clickstream';
import Divider from '@zenbusiness/zui/Divider';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import Text from '@zenbusiness/zui/Text';

import { useCompletionsMutation } from '#client/hooks/useCompletionsMutation';
import { usePromptQuery } from '#client/hooks/usePromptQuery';

import {
  CompletionsContainer,
  SlideInFooter,
  StyledSlide,
  FooterTitle,
  FooterDescription
} from './AiSlideIn.style';
import AiSuggestionList from './AiSuggestionList';

function AiSlideIn({
  title,
  overline,
  subtitle,
  reasonsReplacements,
  promptSlug,
  fillData,
  aiSlideInOpen,
  closeHandler,
  eventingProperties,
  errorMessage,
  footer
}) {
  const [completionsFetched, setCompletionsFetched] = useState(false);

  const { data: promptData, loading: promptLoading, errors: promptErrors } = usePromptQuery({
    promptSlug
  });

  const {
    fetchCompletions,
    data: completionsData,
    loading: completionsLoading,
    errors: completionsErrors
  } = useCompletionsMutation();

  useEffect(() => {
    if (promptData && !promptLoading && !completionsFetched) {
      fetchCompletions(promptData, fillData);
      setCompletionsFetched(true);
    }
  }, [
    promptData,
    promptLoading,
    fillData,
    completionsFetched,
    completionsData,
    fetchCompletions
  ]);

  let content;

  if (promptErrors?.length > 0 || completionsErrors?.length > 0) {
    content = <Text>{errorMessage}</Text>;
  } else if (promptLoading || completionsLoading) {
    content = <LoadingSpinner />;
  } else {
    const {
      reasons: completionReasons,
      introduction,
      conclusion
    } = completionsData?.completions?.messages[0]?.parsed || {};
    const {
      aiProjectName, elementName, elementText, elementValue
    } = eventingProperties;
    content = (
      <>
        <AiSuggestionList
          items={completionReasons}
          titleReplacements={reasonsReplacements}
          header={introduction}
          body={conclusion}
        />
        <ModalDisplayedTracker
          open={aiSlideInOpen && completionsData}
          elementName={elementName || 'smart_assistant'}
          elementText={elementText}
          elementValue={elementValue}
          dynamicProperties={{
            ai_project_name: aiProjectName
          }}
        />
      </>
    );
  }

  return (
    <StyledSlide
      title={title}
      overline={overline}
      subtitle={subtitle}
      open={aiSlideInOpen}
      onClose={closeHandler}
      isLoading={promptLoading || completionsLoading}
    >
      <CompletionsContainer isLoading={promptLoading || completionsLoading}>
        {content}
      </CompletionsContainer>
      <SlideInFooter>
        <Divider />
        <FooterTitle weight="medium" gutterBottom size="xs">
          ZenBusiness AI
        </FooterTitle>
        <FooterDescription size="xs">
          {footer}
        </FooterDescription>
      </SlideInFooter>
    </StyledSlide>
  );
}

AiSlideIn.propTypes = {
  title: PropTypes.string,
  overline: PropTypes.string,
  subtitle: PropTypes.string,
  reasonsReplacements: PropTypes.objectOf(PropTypes.string).isRequired,
  promptSlug: PropTypes.string.isRequired,
  fillData: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array
  ])).isRequired,
  aiSlideInOpen: PropTypes.bool.isRequired,
  closeHandler: PropTypes.func.isRequired,
  eventingProperties: PropTypes.shape({
    elementName: PropTypes.string.isRequired,
    elementText: PropTypes.string,
    elementValue: PropTypes.string,
    aiProjectName: PropTypes.string.isRequired
  }),
  errorMessage: PropTypes.string,
  footer: PropTypes.string
};

export default AiSlideIn;
